import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '../../api';
import Header from '../../components/ui/Header';
import CountdownTimer from './components/CountdownTimer';
import UrgencyBanner from './components/UrgencyBanner';
import DiscountShowcase from './components/DiscountShowcase';
import EventMechanics from './components/EventMechanics';
import ProgressIndicator from './components/ProgressIndicator';
import ExclusiveAccess from './components/ExclusiveAccess';
import AnimatedPromo from './components/AnimatedPromo';
import SocialProof from './components/SocialProof';
import CallToAction from './components/CallToAction';
import VisualEffects from './components/VisualEffects';
import LoadingSpinner from '../../components/LoadingSpinner';

const BlackHourAlert = () => (
  <div className="bg-gradient-to-r from-gray-800 via-purple-900 to-black text-white text-center p-3 shadow-lg rounded-lg">
    <p className="font-bold text-lg flex items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <motion.span
        animate={{
          textShadow: [
            "0 0 5px rgba(192, 132, 252, 0.5)",
            "0 0 15px rgba(192, 132, 252, 0.8)",
            "0 0 5px rgba(192, 132, 252, 0.5)",
          ],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "loop",
          ease: "easeInOut",
        }}
      >
        Deux Black Hours par jour : 10:00 et 20:00 !
      </motion.span>
    </p>
  </div>
);

const BlackHourLimitedEventContent = () => {
  useEffect(() => {
    document.title = 'Black Hour - Offres Limitées | UrbanSpace';
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Visual Effects Background */}
      <VisualEffects />
      {/* Header */}
      <Header />
      {/* Main Content */}
      <motion.main
        className="relative z-10 pt-24 pb-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute top-20 md:top-24 left-1/2 -translate-x-1/2 w-full px-4 max-w-4xl z-20">
            <BlackHourAlert />
        </div>
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {/* Hero Section with Countdown */}
          <motion.section variants={sectionVariants} className="text-center space-y-8">
            <div className="space-y-4">
              <motion.div
                className="inline-flex items-center space-x-2 bg-error/20 border border-error/30 rounded-full px-6 py-2"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div className="w-2 h-2 bg-error rounded-full animate-pulse"></div>
                <span className="font-caption font-bold text-error text-sm">
                  ÉVÉNEMENT EN COURS
                </span>
              </motion.div>
              
              <h1 className="text-5xl lg:text-7xl font-heading font-bold text-foreground">
                BLACK HOUR
                <span className="block bg-gradient-to-r from-accent to-error bg-clip-text text-transparent">
                  ÉVÉNEMENT LIMITÉ
                </span>
              </h1>
              
              <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
                Profitez de réductions exceptionnelles jusqu'à -70% sur notre collection streetwear exclusive. 
                Offre limitée dans le temps - Dépêchez-vous !
              </p>
            </div>
            
            <CountdownTimer />
          </motion.section>


          
          {/* Discount Showcase */}
          <motion.section variants={sectionVariants}>
            <DiscountShowcase />
          </motion.section>

          {/* Progress Indicator */}
          <motion.section variants={sectionVariants}>
            <ProgressIndicator />
          </motion.section>

          
          {/* Event Mechanics & Rules */}
          <motion.section variants={sectionVariants}>
            <EventMechanics />
          </motion.section>

          {/* Social Proof & Testimonials */}
          <motion.section variants={sectionVariants}>
            <SocialProof />
          </motion.section>

          {/* Final Call to Action */}
          <motion.section variants={sectionVariants}>
            <CallToAction />
          </motion.section>
        </div>
      </motion.main>

    </div>
  );
};

const BlackHourLimitedEvent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkEventStatus = async () => {
      try {
        const response = await eventsApi.checkStatus();
        
        if (response.success && response.events.black_hour) {
          // Si l'événement est actif, rediriger vers la page principale
          navigate('/black-hour', { replace: true });
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to check event status:', error);
        setIsLoading(false);
      }
    };

    checkEventStatus();
  }, [navigate]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return <BlackHourLimitedEventContent />;
};

export default BlackHourLimitedEvent;